import { groupTitle } from './groups.js';
/**
 * Имена объектов и шагов запуска — по-человечески. Технические части (метка
 * запуска `#ml-…`, локальные ссылки `s0/a1v0`, коды шагов `activate.ad`) в
 * тексте не показываются — только в подсказке (`title`) или мелкой строкой
 * «метка».
 */
const MARKER_RE = /\s*(#ml-[A-Za-z0-9]+)(?:\/(\S+))?\s*$/;
/**
 * «ST_t1EN_BC25_1 #ml-e9c74966/s0» → { name: «ST_t1EN_BC25_1», marker:
 * «#ml-e9c74966», ref: «s0» }. Имя без метки — как есть.
 */
export function splitObjectName(raw) {
    const m = MARKER_RE.exec(raw);
    if (!m)
        return { name: raw, marker: null, ref: null };
    const name = raw.slice(0, m.index).trim();
    return { name: name === '' ? raw : name, marker: m[1] ?? null, ref: m[2] ?? null };
}
/** Имя объекта без технической метки. */
export function displayName(raw) {
    if (raw == null)
        return null;
    return splitObjectName(raw).name;
}
const ADSET_RE = /^s(\d+)$/;
const AD_RE = /^s(\d+)\/a(\d+)(?:v(\d+))?$/;
export function localRefLabel(ref, kind, groups) {
    if (ref == null || ref === '')
        return '';
    // группы шаблона (v4 §3.1): «① LAL23 · копия 2» вместо «Группа объявлений N»
    if (groups && groups.length > 0) {
        const set = /^(s\d+)(\/.*)?$/.exec(ref);
        const g = set ? groups.find((x) => x.localRef === set[1]) : undefined;
        if (set && g) {
            const order = [];
            for (const x of groups)
                if (!order.includes(x.groupKey))
                    order.push(x.groupKey);
            const copies = new Set(groups.map((x) => x.copyIndex)).size > 1;
            const head = `${groupTitle(g.index ?? order.indexOf(g.groupKey), g.label)}${copies ? ` · копия ${g.copyIndex + 1}` : ''}`;
            if (set[2] == null)
                return head;
            const tail = localRefLabel(ref, kind);
            return `${head} · ${tail.replace(/^Группа объявлений \d+ · /, '')}`;
        }
    }
    if (ref === 'campaign')
        return 'Кампания';
    if (ref === 'adlabel' || ref === 'verify' || ref === 'batch')
        return '';
    if (/^(vid|img)-/.test(ref))
        return '';
    const s = ADSET_RE.exec(ref);
    if (s)
        return `Группа объявлений ${Number(s[1]) + 1}`;
    const a = AD_RE.exec(ref);
    if (a) {
        const item = Number(a[2]) + 1;
        const variant = a[3] != null ? ` (текст ${Number(a[3]) + 1})` : '';
        if (baseKind(kind) === 'creative')
            return `Креатив ${item}${variant}`;
        return `Группа объявлений ${Number(a[1]) + 1} · Объявление ${item}${variant}`;
    }
    return ref;
}
function baseKind(kind) {
    if (!kind)
        return '';
    const parts = kind.split('.');
    return parts[parts.length - 1] ?? kind;
}
const OBJECT_RU = {
    campaign: 'Кампания',
    adset: 'Группа',
    ad: 'Объявление',
    creative: 'Креатив',
    adlabel: 'Метка запуска',
    image: 'Картинка',
    video: 'Видео',
};
/** Род объекта в родительном падеже — для «Включение группы», «Откат кампании». */
const OBJECT_GEN_RU = {
    campaign: 'кампании',
    adset: 'группы',
    ad: 'объявления',
    creative: 'креатива',
    adlabel: 'метки запуска',
    image: 'картинки',
    video: 'видео',
    media: 'медиа',
};
/** Шаги без префикса: код → подпись. */
const STEP_RU = {
    adlabel: 'Метка запуска',
    label: 'Метка запуска',
    media: 'Загрузка медиа',
    'media.video': 'Загрузка видео',
    'media.image': 'Загрузка картинки',
    image: 'Загрузка картинки',
    video: 'Загрузка видео',
    creative: 'Креатив',
    campaign: 'Кампания',
    adset: 'Группа',
    ad: 'Объявление',
    verify: 'Проверка',
    activate: 'Включение',
    post_activate_verify: 'Проверка после включения',
    'post-activate-verify': 'Проверка после включения',
    postactivate: 'Проверка после включения',
    'verify.post_activate': 'Проверка после включения',
    'verify.postactivate': 'Проверка после включения',
    rollback: 'Откат',
    pause: 'Пауза',
};
/** Префиксы «действие.объект»: `activate.ad` → «Включение объявления». */
const STEP_PREFIX_RU = {
    activate: 'Включение',
    rollback: 'Откат',
    delete: 'Удаление',
    pause: 'Пауза',
    media: 'Загрузка',
    verify: 'Проверка',
};
/**
 * Код шага auto-ads → подпись по-русски (одна карта на раздел). Незнакомый
 * код — как есть, чтобы новый шаг был виден.
 */
export function stepLabelRu(kind) {
    const direct = STEP_RU[kind];
    if (direct)
        return direct;
    const dot = kind.indexOf('.');
    if (dot > 0) {
        const prefix = STEP_PREFIX_RU[kind.slice(0, dot)];
        const obj = OBJECT_GEN_RU[kind.slice(dot + 1)];
        if (prefix && obj)
            return `${prefix} ${obj}`;
    }
    return kind;
}
/** Род объекта сверки по-русски («Кампания»); незнакомый — как есть. */
export function objectKindRu(kind) {
    return OBJECT_RU[kind] ?? kind;
}
/**
 * Объект сверки словами: «Кампания», «Группа объявлений 1», «Группа объявлений 1 · Объявление 1»,
 * «Креатив 1». Ссылка не разобралась — «<род> <ссылка>».
 */
export function objectLabel(kind, ref) {
    const label = localRefLabel(ref, kind);
    if (label === '' || label === ref) {
        const k = objectKindRu(kind);
        return ref === '' || ref === kind ? k : `${k} ${ref}`;
    }
    if (kind === 'campaign' || kind === 'creative' || kind === 'ad' || kind === 'adset')
        return label;
    return `${objectKindRu(kind)} · ${label}`;
}
/** Вид объекта Meta шага или сверки (`create.adset` → `adset`) — для «Скопировать id группы». */
export function metaObjectKind(kind) {
    const base = kind?.split('.').pop();
    return base === 'campaign' || base === 'adset' || base === 'ad' || base === 'creative'
        ? base
        : undefined;
}
/**
 * Запасные подписи раскладки пачки (на экране подпись берётся из реестра
 * auto-ads; эти — если её там нет). Дефект 4 (v4 §11.9): без «N» и «1-N-1».
 */
export const LAYOUT_LABELS = {
    campaign_per_item: 'Кампания на каждый креатив',
    adsets_in_campaign: '1 кампания — группа объявлений на каждый креатив и копию',
    ads_in_adset: '1 кампания — 1 группа объявлений — N объявлений (тест креативов)',
};
//# sourceMappingURL=naming.js.map